import { ILayoutBaseTestGen } from '../layoutBaseTest';
import {
  PdfGen,
  PdfFont,
  PdfRect,
  PdfBrush,
  ETextAlign,
  PdfUnit,
  PdfSize,
  PageGen,
} from '../../pdf/pdf';
import '../../pdf/draw/string';
import '../../pdf/draw/primitives';
import { render, parse, BookOptions } from '../../pdf/book';
import { OptsBaseTest } from '../optsBaseTest';

const MM = PdfUnit.FromMillimeters;
const MARGIN = MM(12);

export class TestBook implements ILayoutBaseTestGen {
  async generate(pdf: PdfGen, opts: OptsBaseTest): Promise<void> {
    await pdf.addStandardFonts();

    const markdown = `
# Chapter One: Introduction to Typesetting

This is the first chapter of our comprehensive test book. It contains multiple paragraphs to demonstrate the book rendering system including all supported features such as **bold text**, *italic text*, and \`inline code\`. The system also supports LaTeX math like $$\\alpha + \\beta = \\gamma$$ and display math like $$\\int_{0}^{\\infty} e^{-x^2} dx = \\frac{\\sqrt{\\pi}}{2}$$.

## Section 1.1: Getting Started

This is a subsection within the first chapter. We can include various types of content to fully test the rendering capabilities of this book typesetting system[^: This is a footnote that appears at the bottom of the page.].

### Unordered Lists

We can include unordered lists:

- First item at the top level
- Second item at the top level
- Third item at the top level
  - First nested item (indented)
  - Second nested item (indented)
    - Deeply nested item
    - Another deeply nested item
  - Third nested item
- Back to top level again
  - Another nested list
- Final top level item

### Ordered Lists

We can also include ordered lists with numbers:

1. First numbered item
2. Second numbered item
3. Third numbered item
   1. Nested numbered item
   2. Another nested numbered item
   3. Yet another nested numbered item
4. Back to main level
   1. Another nested list with numbers
   2. Continuing the list
5. Final numbered item

### Mixed Nested Lists

We can combine ordered and unordered lists:

- Unordered item A
- Unordered item B
  1. Nested ordered item 1
  2. Nested ordered item 2
- Back to unordered
  - Deep unordered
    1. Mixed deep ordered
    2. Mixed deep ordered
- Final item

## Section 1.2: Code Blocks

And we can have code blocks with syntax highlighting hints:

\`\`\`javascript
function fibonacci(n) {
  if (n <= 1) return n;
  return fibonacci(n - 1) + fibonacci(n - 2);
}

function quickSort(arr) {
  if (arr.length <= 1) return arr;
  const pivot = arr[arr.length - 1];
  const left = arr.filter((el, i) => el < pivot && i < arr.length - 1);
  const right = arr.filter((el, i) => el >= pivot && i < arr.length - 1);
  return [...quickSort(left), pivot, ...quickSort(right)];
}
\`\`\`

This paragraph has a footnote[^: This is a footnote that appears at the bottom of the page.].

## Section 1.3: Mathematical Formulas

The book typesetting system supports complex mathematical expressions:

### Inline Mathematics

Einstein's famous equation $$E = mc^2$$ demonstrates the relationship between mass and energy. The quadratic formula $$x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}$$ gives the solutions to quadratic equations. The sum of an arithmetic series is $$S_n = \\frac{n(a_1 + a_n)}{2}$$.

### Display Mathematics

Display math is centered and larger:

$$\\sum_{i=1}^{n} i = \\frac{n(n+1)}{2}$$

This formula calculates the sum of the first $n$ natural numbers. Another important formula is the Taylor series expansion:

$$e^x = 1 + x + \\frac{x^2}{2!} + \\frac{x^3}{3!} + \\cdots = \\sum_{n=0}^{\\infty} \\frac{x^n}{n!}$$

The Fibonacci sequence can be expressed using Binet's formula[^: Fibonacci numbers appear throughout nature and have many applications in computer science.]:

$$F_n = \\frac{\\phi^n - \\psi^n}{\\phi - \\psi} = \\frac{\\phi^n - \\psi^n}{\\sqrt{5}}$$

where $\\phi = \\frac{1 + \\sqrt{5}}{2}$ (the golden ratio) and $\\psi = \\frac{1 - \\sqrt{5}}{2}$.

# Chapter Two: Advanced Features

This is the second chapter with more advanced content including tables, citations, and various inline elements.

## Section 2.1: Large Tables

Tables are essential for presenting structured data. Here's a larger table:

| Rank | Language    | Created By              | Year | Paradigm           | Typing       |
|------|-------------|-------------------------|------|--------------------|--------------|
| 1    | Python      | Guido van Rossum        | 1991 | Multi-paradigm    | Dynamic     |
| 2    | JavaScript  | Brendan Eich            | 1995 | Event-driven      | Dynamic     |
| 3    | Java        | James Gosling          | 1995 | Object-oriented   | Static      |
| 4    | C++         | Bjarne Stroustrup      | 1985 | Multi-paradigm    | Static      |
| 5    | C#          | Anders Hejlsberg       | 2000 | Object-oriented   | Static      |
| 6    | Go          | Robert Griesemer       | 2009 | Concurrent        | Static      |
| 7    | Rust        | Graydon Hoare          | 2010 | Multi-paradigm    | Static      |
| 8    | TypeScript  | Microsoft               | 2012 | Object-oriented   | Static      |
| 9    | Ruby        | Yukihiro Matsumoto     | 1995 | Object-oriented   | Dynamic     |
| 10   | Swift       | Apple                  | 2014 | Multi-paradigm    | Static      |

This table demonstrates various column alignments and content types[^: This table shows popular programming languages with their key characteristics.].

### Another Table: Mathematical Constants

| Constant       | Symbol      | Approximate Value | Definition                     |
|----------------|-------------|------------------|--------------------------------|
| Pi             | $$\\pi$$      | 3.1415926535     | Ratio of circumference to diameter |
| Euler's number | $$e$$         | 2.7182818284     | Base of natural logarithm     |
| Golden ratio   | $$\\phi$$     | 1.6180339887     | $\\frac{1+\\sqrt{5}}{2}$      |
| Square root of 2 | $$\\sqrt{2}$$ | 1.4142135623   | Diagonal of unit square       |
| Imaginary unit | $$i$$         | $$\\sqrt{-1}$$     | Square root of negative one   |

## Section 2.2: Citations and References

The book renderer supports citations in various formats:

The TeXbook by Donald Knuth is the definitive guide to the TeX typesetting system[@knuth1984: Donald Knuth. The TeXbook. Addison-Wesley, 1984.]. Another important work is "The LaTeX Companion"[@latexcompanion: Michel Goossens, Frank Mittelbach, Alexander Samarin. The LaTeX Companion. Addison-Wesley, 1994.].

For web technologies, MDN provides excellent documentation[@mdn: Mozilla Developer Network. https://developer.mozilla.org/]. The Python documentation[@python: Python Software Foundation. Python Language Reference. https://www.python.org/] is comprehensive and well-maintained.

Academic citations follow standard formats[@citation1: First Author. Title of Paper. Journal Name, Volume(Issue), Pages, Year.]. Multiple citations can appear in the same location[@citation2: Second Author. Another Paper. Conference Proceedings, Year.; @citation3: Third Author. Yet Another Paper. arXiv preprint, Year.].

## Section 2.3: More Features

### Inline Code and Emphasis

We can combine various inline elements: **bold and \`code\`**, *italic and \`code\`*, ***bold italic and code***, and \`\`different code styles\`\`.

### Links and References

The system supports links to external resources like [Wikipedia](https://wikipedia.org) and [GitHub](https://github.com).

## Section 2.4: Extended Lists

### Numbered Lists with Long Content

1. This is a very long item that contains multiple sentences to test how the list item wraps across multiple lines. The text should wrap cleanly and maintain proper indentation.
2. Another long item with **multiple formatting** options including *italic*, \`code\`, and [links](https://example.com). This tests the full range of inline formatting within list items[^: A footnote within a list item.].
3. The third item continues the pattern with even more content to ensure the list renders correctly across multiple lines and pages if necessary.

### Definition Lists

Term 1
: This is the definition of term 1. It can span multiple lines and contain various formatting.

Term 2
: Another definition with code examples: \`const x = 42;\`

# Chapter Three: Mathematics and Science

This chapter focuses on mathematical and scientific content to test display math and complex formatting.

## Section 3.1: Calculus

The derivative of a function $f(x)$ is defined as:

$$f'(x) = \\lim_{h \\to 0} \\frac{f(x+h) - f(x)}{h}$$

Integration by parts formula:

$$\\int u \\, dv = uv - \\int v \\, du$$

The Fundamental Theorem of Calculus connects differentiation and integration:

$$\\int_a^b f(x) \\, dx = F(b) - F(a)$$

where $F$ is an antiderivative of $f$[^: This is a crucial theorem in calculus that relates the integral and derivative.].

## Section 3.2: Linear Algebra

Matrix multiplication is defined as:

$$(AB)_{ij} = \\sum_{k=1}^{n} a_{ik} b_{kj}$$

The determinant of a 2×2 matrix:

$$\\det \\begin{pmatrix} a & b \\\\ c & d \\end{pmatrix} = ad - bc$$

Eigenvalues and eigenvectors satisfy:

$$A \\mathbf{v} = \\lambda \\mathbf{v}$$

## Section 3.3: Physics Equations

The Schrödinger equation in quantum mechanics:

$$i\\hbar \\frac{\\partial}{\\partial t} \\Psi(\\mathbf{r}, t) = \\hat{H} \\Psi(\\mathbf{r}, t)$$

Newton's second law:

$$\\mathbf{F} = m\\mathbf{a}$$

The Lorentz force:

$$\\mathbf{F} = q(\\mathbf{E} + \\mathbf{v} \\times \\mathbf{B})$$

## Section 3.4: Statistics

The normal distribution probability density function:

$$f(x) = \\frac{1}{\\sigma\\sqrt{2\\pi}} e^{-\\frac{1}{2}\\left(\\frac{x-\\mu}{\\sigma}\\right)^2}$$

Standard deviation formula:

$$\\sigma = \\sqrt{\\frac{1}{N} \\sum_{i=1}^{N} (x_i - \\mu)^2}$$

# Chapter Four: Computer Science

This chapter covers computer science topics to test code blocks and technical content.

## Section 4.1: Algorithms

### Binary Search Algorithm

\`\`\`
function binarySearch(arr, target):
    left = 0
    right = arr.length - 1
    
    while left <= right:
        mid = (left + right) // 2
        if arr[mid] == target:
            return mid
        else if arr[mid] < target:
            left = mid + 1
        else:
            right = mid - 1
    
    return -1
\`\`\`

### Sorting Algorithms Comparison

| Algorithm   | Best      | Average   | Worst    | Space    |
|-------------|-----------|-----------|----------|----------|
| Quick Sort  | $$O(n\\log n)$$ | $$O(n\\log n)$$ | $$O(n^2)$$ | $$O(\\log n)$$ |
| Merge Sort  | $$O(n\\log n)$$ | $$O(n\\log n)$$ | $$O(n\\log n)$$ | $$O(n)$$ |
| Heap Sort   | $$O(n\\log n)$$ | $$O(n\\log n)$$ | $$O(n\\log n)$$ | $$O(1)$$ |
| Bubble Sort | $$O(n)$$      | $$O(n^2)$$    | $$O(n^2)$$    | $$O(1)$$   |

## Section 4.2: Data Structures

### Binary Tree Traversal

In-order traversal visits nodes in sorted order for BSTs. Pre-order is useful for copying trees. Post-order is used for deleting trees[^: Tree traversals have different applications depending on the use case.].

\`\`\`python
class TreeNode:
    def __init__(self, val):
        self.val = val
        self.left = None
        self.right = None

def inorder(root):
    return inorder(root.left) + [root.val] + inorder(root.right) if root else []
\`\`\`

### Hash Table

Hash tables provide $$O(1)$$ average case lookup time. The hash function distributes keys across buckets:

$$h(k) = k \\bmod m$$

where $$m$$ is the number of buckets.

## Section 4.3: Complexity Analysis

Big O notation describes the upper bound of time complexity:

- $$O(1)$$ - Constant time
- $$O(\\log n)$$ - Logarithmic time
- $$O(n)$$ - Linear time
- $$O(n\\log n)$$ - Linearithmic time
- $$O(n^2)$$ - Quadratic time
- $$O(2^n)$$ - Exponential time[^: Exponential algorithms become impractical for large inputs.]

# Chapter Five: Conclusion

This is the final chapter. The book renderer supports page breaks, footnotes at page bottoms, and endnotes at chapter ends.

## Section 5.1: Summary

Throughout this test book, we have demonstrated:

1. Various heading levels (H1 through H6 where supported)
2. Paragraph formatting with proper line spacing
3. Unordered lists with multiple nesting levels
4. Ordered lists with multiple nesting levels
5. Mixed lists combining ordered and unordered
6. Code blocks with various programming languages
7. Inline code and formatted text
8. Tables of various sizes and complexity
9. Footnotes throughout the text[^: This is another footnote to test multiple footnotes on one page.]
10. Citations in multiple formats
11. Inline mathematical expressions
12. Display mathematical equations
13. Links to external resources

## Section 5.2: Final Notes

The book typesetting system is designed to produce professional-quality output with proper typesetting conventions including:

- Proper hyphenation and line breaking
- Kerning and ligatures where applicable
- Page breaks that respect content structure
- Footnotes that appear at the bottom of pages
- Tables that flow properly within page boundaries

For any questions or further development, please refer to the project documentation[@projectdocs: EduCraft Project Documentation. https://github.com/educraft/educraft].

Thank you for testing the book typesetting system!
`.trim();

    const options: BookOptions = {
      fontSize: 11,
      lineHeight: 1.5,
      margin: 20,
      columns: 1,
    };

    await render(pdf, markdown, options);
  }
}
